import cn from 'classnames';
import { FC, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  SharingInvitation,
  SharingInvitationStatus,
} from '@/shared/api/protocol-ts/model/dto_sharing_pb';
import { useAppDispatch } from '@/shared/hooks';
import { Icon } from '@/shared/ui';

import { ModalID, modalModel } from '@/entities/modal';

import styles from './SharedDoctorRow.module.scss';

type SharedDoctorRowProps = {
  sharedPatientInvitation: SharingInvitation;
  canCancelSharing: boolean;
  className?: string;
  testID?: string;
  lessInfo?: boolean;
  isMobile?: boolean;
  openShowAllModal?: () => void;
};

export const SharedDoctorRow: FC<SharedDoctorRowProps> = (props) => {
  const {
    sharedPatientInvitation,
    canCancelSharing,
    className,
    testID,
    lessInfo,
    isMobile,
    openShowAllModal,
  } = props;

  const { formatDate } = useIntl();

  const dispatch = useAppDispatch();

  const [copied, setCopied] = useState(false);

  const email = sharedPatientInvitation.Recipient?.Email;

  const accessCode = sharedPatientInvitation.Secret;

  const patientID = sharedPatientInvitation.PatientID;

  const isInviteAccepted =
    sharedPatientInvitation.Status ===
    SharingInvitationStatus.SharingInvitationStatus_Accepted;

  // NOTE: if the user with whom the patient was shared with AutoAccepted but doesn't yet have an account (meaning they haven't registered), the status will be Pending
  const isAccepted = isInviteAccepted || sharedPatientInvitation.AutoAccept;

  const openRevokeSharedPatientModal = () => {
    if (patientID) {
      dispatch(
        modalModel.actions.openModal({
          modalID: ModalID.RevokeSharedPatientModal,
          data: {
            patientID,
            sharedPatientDoctorID: sharedPatientInvitation.Recipient?.UserID,
            invitationID: sharedPatientInvitation.ID,
            // NOTE: for revoking, the InvitationStatus is crucial to either revoke the invitation or cancel the sharing
            isInviteAccepted,
          },
        }),
      );
    }
  };

  const copyToClipBoard = async () => {
    try {
      await navigator.clipboard.writeText(accessCode);

      setCopied(true);

      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const statusIconName = isAccepted ? 'check' : 'time';
  const statusIconSize = isAccepted ? 24 : 32;

  if (isMobile) {
    return (
      <div
        className={cn(
          styles.container,
          !isAccepted && styles.waitForAccept,
          className,
        )}
        data-testid={testID}
        onClick={openRevokeSharedPatientModal}
      >
        <p className={cn(styles.email, 'p2')}>{email}</p>
        {!isAccepted && <Icon name="time" size={32} />}
      </div>
    );
  }

  return (
    <div
      className={cn(styles.container, className)}
      data-testid={testID}
      data-component="shared-doctor-row"
      onClick={openShowAllModal}
    >
      <div
        className={cn(styles.statusIcon, isAccepted && styles.isInviteAccepted)}
      >
        <Icon name={statusIconName} size={statusIconSize} />
      </div>

      <div className={cn(styles.content, lessInfo && styles.showLess)}>
        <p className={cn(styles.email, 'p2')}>{email}</p>

        {!lessInfo && (
          <>
            <p className={cn(styles.sharedDataList, 'p3')}>
              <FormattedMessage
                id="sharedDoctor.sharingDate"
                defaultMessage="Sharing Date:"
              />

              {sharedPatientInvitation.Created?.At ? (
                <span className={styles.sharedInfo}>
                  {sharedPatientInvitation &&
                    formatDate(
                      new Date(sharedPatientInvitation.Created.At.toDate()),
                      {
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: 'numeric',
                      },
                    )}
                </span>
              ) : null}
            </p>

            {!isAccepted && (
              <p className={cn(styles.sharedDataList, 'p3')}>
                <FormattedMessage
                  id="sharedDoctor.accessCode"
                  defaultMessage="Access code:"
                />
                <span className={styles.sharedInfo}>{accessCode}</span>

                <Icon
                  name={copied ? 'check' : 'copy'}
                  size={18}
                  onClick={copyToClipBoard}
                  className={cn(!copied && styles.copyIcon)}
                />
              </p>
            )}
          </>
        )}
      </div>

      <div className={styles.controls}>
        {canCancelSharing && (
          <button
            type="button"
            className={styles.button}
            onClick={(event) => {
              event.stopPropagation();
              openRevokeSharedPatientModal();
            }}
          >
            <Icon name="delete" size={32} />
          </button>
        )}
      </div>
    </div>
  );
};
